"use client";

import { useEffect, useState } from "react";
import { MessageSquareText } from "lucide-react";
import { SlackTrustedSendersSettings } from "./SlackTrustedSendersSettings";

type PolicyState = {
  connected: boolean;
  policy: "audience" | "internal_metadata";
  updated_at: string | null;
};

/** Workspace admins opt in explicitly; rendering or loading never changes policy. */
export function SlackPreviewSettings({ workspaceId }: { workspaceId: string }) {
  const endpoint = `/api/workspaces/${encodeURIComponent(workspaceId)}/slack-preview-policy`;
  const [state, setState] = useState<PolicyState | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [policyRevision, setPolicyRevision] = useState(0);
  const [notice, setNotice] = useState<{ error: boolean; text: string } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetch(endpoint, { cache: "no-store", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 403
          ? "Only workspace owners and admins can manage Slack previews."
          : "Could not load Slack preview settings. Reopen settings to try again.");
        const data = await response.json() as PolicyState;
        if (controller.signal.aborted) return;
        setState(data);
        setEnabled(data.policy === "internal_metadata");
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setNotice({ error: true, text: error instanceof Error ? error.message : "Could not load Slack preview settings." });
      });
    return () => controller.abort();
  }, [endpoint]);

  async function save() {
    setSaving(true);
    setNotice(null);
    try {
      const response = await fetch(endpoint, {
        method: "PATCH", headers: { "content-type": "application/json" },
        body: JSON.stringify({ policy: enabled ? "internal_metadata" : "audience" }),
      });
      if (!response.ok) throw new Error(response.status === 403
        ? "Only workspace owners and admins can change this setting."
        : "Could not save Slack preview settings. Please try again.");
      const data = await response.json() as PolicyState;
      setState(data);
      setEnabled(data.policy === "internal_metadata");
      setNotice({ error: false, text: "Slack preview settings saved." });
      setPolicyRevision((current) => current + 1);
    } catch (error) {
      setNotice({ error: true, text: error instanceof Error ? error.message : "Could not save Slack preview settings." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <section aria-labelledby="slack-preview-heading" className="border-b border-[var(--border)] px-5 py-4">
      <h4 id="slack-preview-heading" className="mb-3 flex items-center gap-2 text-[13px] font-medium">
        <MessageSquareText size={14} className="text-[var(--fg-tertiary)]" />
        Slack previews
      </h4>
      {!state && !notice && <p className="text-[12px] text-[var(--fg-tertiary)]">Loading Slack settings…</p>}
      {state && !state.connected && <p className="text-[12px] text-[var(--fg-tertiary)]">Connect the Pauta Slack app to this workspace to manage previews.</p>}
      {state?.connected && <>
        <label className="flex items-start gap-2 text-[13px] font-medium">
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} disabled={saving}
            aria-describedby="slack-preview-description" className="mt-1 accent-[var(--accent)]" />
          Share internal task and document previews
        </label>
        <p id="slack-preview-description" className="mt-2 text-[12px] leading-5 text-[var(--fg-tertiary)]">
          Allow linked members to share titles, task status, owners and project names in internal Slack channels where Pauta is invited.
          People who can read those channels can see previews even without Pauta access.
          Opening a task or document still requires permission in Pauta. Document content and Slack Connect channels are excluded.
        </p>
        <p className="mt-2 text-[12px] leading-5 text-[var(--fg-tertiary)]">
          When off, private previews are limited to verified recipients in direct messages and private channels.
          Changing this setting affects future previews; it does not remove messages already shared.
        </p>
        <button type="button" onClick={() => void save()} disabled={saving || enabled === (state.policy === "internal_metadata")}
          className="mt-3 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-1.5 text-[12px] font-medium hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save Slack previews"}
        </button>
      </>}
      {notice && <p role={notice.error ? "alert" : "status"} className={`mt-2 text-[12px] ${notice.error ? "text-[var(--red)]" : "text-[var(--fg-secondary)]"}`}>{notice.text}</p>}
    </section>
    <SlackTrustedSendersSettings workspaceId={workspaceId} refreshKey={policyRevision} />
    </>
  );
}
